import { createSignal, createContext, useContext, onMount, onCleanup, Show, For } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { initTweaks, TweaksPanel } from "./tweaks";
import { useNotify } from "../shared/notify";
import { PwaInstallButton } from "../shared/pwa-install";
import { CommandPalette, type CommandItem } from "../command-palette";
import type { JSX } from "solid-js";

// ── Sidebar mobile context ─────────────────────────────────────────────────
interface SidebarCtx {
  open: () => boolean;
  setOpen: (v: boolean) => void;
}
const SidebarContext = createContext<SidebarCtx>({ open: () => false, setOpen: () => {} });
export function useSidebarContext() {
  return useContext(SidebarContext);
}

interface ConsoleShellProps {
  children: JSX.Element;
  /** email/nom affiché dans le menu utilisateur */
  userEmail?: string;
  /** rôles de l'utilisateur pour conditionner les liens d'administration */
  userRoles?: string[];
}

// Liens d'administration affichés dans le menu utilisateur. Filtrés par rôle.
// Le logo "R" ramène au cockpit (/console/session) ; la navigation entre
// pages admin se fait via ce menu, plus de topnav à onglets.
function getAdminLinks(
  roles?: string[],
): Array<{ id: string; label: string; path: string; icon: string; superAdmin?: boolean }> {
  const links: Array<{ id: string; label: string; path: string; icon: string; superAdmin?: boolean }> = [
    { id: "clients", label: "Clients & sites", path: "/console/clients", icon: "🏢" },
    { id: "sessions", label: "Sessions", path: "/console/sessions", icon: "📺" },
    { id: "audit", label: "Audit", path: "/console/audit", icon: "📋" },
    { id: "settings", label: "Réglages", path: "/console/settings", icon: "⚙" },
    { id: "super-admin", label: "Super Admin", path: "/console/super-admin", icon: "⭐", superAdmin: true },
  ];
  return links.filter((l) => !l.superAdmin || roles?.includes("super_admin"));
}

export function ConsoleShell(props: ConsoleShellProps) {
  const navigate = useNavigate();
  const notify = useNotify();
  const [tweaksOpen, setTweaksOpen] = createSignal(false);
  const [sidebarOpen, setSidebarOpen] = createSignal(false);
  const [userMenuOpen, setUserMenuOpen] = createSignal(false);

  function navTo(path: string) {
    setSidebarOpen(false);
    setUserMenuOpen(false);
    navigate(path);
  }

  // Une fois le bundle serveur détecté plus récent que le local, on émet un
  // toast sticky avec une action "Recharger" — plus visible que l'ancien
  // bandeau plein écran et empilable avec d'autres notifications.
  let versionNotified = false;

  // Palette de commandes (Ctrl+K / Cmd+K) — navigue vers les pages admin
  // et ramène au cockpit. Reprise de la "search" du topnav.
  const [paletteOpen, setPaletteOpen] = createSignal(false);
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });
  const paletteItems = (): CommandItem[] => {
    const items: CommandItem[] = [
      {
        id: "cockpit",
        label: "Cockpit (prise en main)",
        hint: "Onglet · /console/session",
        category: "Navigation",
        onSelect: () => navTo("/console/session"),
      },
    ];
    for (const link of adminLinks()) {
      items.push({
        id: `admin:${link.id}`,
        label: `${link.icon} ${link.label}`,
        hint: `Onglet · ${link.path}`,
        category: "Administration",
        onSelect: () => navTo(link.path),
      });
    }
    return items;
  };

  // ── Détection de nouvelle build déployée ─────────────────────────────
  const initialBundle = (() => {
    const scripts = Array.from(document.scripts);
    for (const s of scripts) {
      const m = s.src.match(/\/dist-start\/console\.([A-Za-z0-9_-]+)\.js/);
      if (m) return m[1];
    }
    return null;
  })();

  onMount(() => {
    initTweaks();
    const check = async () => {
      try {
        const r = await fetch("/api/version", { cache: "no-store" });
        if (!r.ok) return;
        const d = (await r.json()) as { bundle?: string };
        if (d.bundle && initialBundle && d.bundle !== initialBundle && !versionNotified) {
          versionNotified = true;
          notify.info("Nouvelle version déployée", {
            hint: `Bundle ${initialBundle} → ${d.bundle}`,
            sticky: true,
            action: { label: "Recharger", run: () => window.location.reload() },
          });
        }
      } catch {
        /* network down — silencieux */
      }
    };
    check();
    const id = setInterval(check, 15000);
    onCleanup(() => clearInterval(id));
  });

  const adminLinks = () => getAdminLinks(props.userRoles);

  return (
    <SidebarContext.Provider value={{ open: sidebarOpen, setOpen: setSidebarOpen }}>
      <div class="console-app">
        {/* ── HEADER (minimaliste, sans topnav à onglets) ── */}
        <nav class="console-topnav console-topnav--slim">
          {/* Hamburger mobile */}
          <button class="console-hamburger" aria-label="Menu" onClick={() => setSidebarOpen((v) => !v)}>
            ☰
          </button>

          {/* Logo "R" → cockpit (prise en main) */}
          <a class="console-topnav-brand" href="/console/session" title="Retour au cockpit">
            <div class="console-topnav-logo">R</div>
            <span class="console-topnav-name">RemoteWS</span>
          </a>

          {/* Bouton retour cockpit */}
          <button
            class="console-back-cockpit"
            type="button"
            onClick={() => navTo("/console/session")}
            title="Retour au cockpit"
          >
            ← Cockpit
          </button>

          <div class="console-topnav-spacer" />

          {/* Search → command palette */}
          <button
            type="button"
            class="console-topnav-search console-topnav-search--btn"
            onClick={() => setPaletteOpen(true)}
            title="Recherche / commandes — Ctrl+K"
            aria-label="Ouvrir la palette de commandes"
          >
            <span class="console-topnav-search-icon">⌕</span>
            <span class="console-topnav-search-text">Rechercher…</span>
            <kbd class="console-topnav-search-kbd">⌘K</kbd>
          </button>

          {/* Menu utilisateur : email + liens administration + déconnexion */}
          <div class="console-topnav-user">
            <button
              class="console-topnav-user-btn"
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={userMenuOpen()}
            >
              <span>{props.userEmail ?? "admin"}</span>
              <span class="console-topnav-user-chevron" aria-hidden="true">
                ▼
              </span>
            </button>
            <Show when={userMenuOpen()}>
              <div class="user-dropdown-overlay" onClick={() => setUserMenuOpen(false)} />
              <div class="user-dropdown-menu">
                <div class="user-dropdown-item user-dropdown-email">{props.userEmail ?? "admin"}</div>
                <div class="user-dropdown-divider" />
                <div class="user-dropdown-section">Administration</div>
                <For each={adminLinks()}>
                  {(link) => (
                    <button class="user-dropdown-item" type="button" onClick={() => navTo(link.path)}>
                      {link.icon} {link.label}
                    </button>
                  )}
                </For>
                <div class="user-dropdown-divider" />
                <button
                  class="user-dropdown-item"
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setTweaksOpen(true);
                  }}
                >
                  ⚙ Tweaks
                </button>
                <button
                  class="user-dropdown-item user-dropdown-danger"
                  type="button"
                  onClick={async () => {
                    setUserMenuOpen(false);
                    try {
                      await fetch("/api/auth/sign-out", { credentials: "include" });
                    } catch {
                      /* ignore */
                    }
                    window.location.href = "/login";
                  }}
                >
                  Se déconnecter
                </button>
              </div>
            </Show>
          </div>

          <PwaInstallButton />

          <button class="console-topnav-tweaks" onClick={() => setTweaksOpen((v) => !v)}>
            ⚙ Tweaks
          </button>
        </nav>

        {/* ── TWEAKS ── */}
        <Show when={tweaksOpen()}>
          <TweaksPanel onClose={() => setTweaksOpen(false)} />
        </Show>

        {/* ── CONTENU ── */}
        <div class="console-body">{props.children}</div>

        {/* Command palette — navigation entre cockpit et pages admin. */}
        <CommandPalette open={paletteOpen} items={paletteItems} onClose={() => setPaletteOpen(false)} />
      </div>
    </SidebarContext.Provider>
  );
}
